import React, {Fragment} from "react"
import clsx from "clsx"
import {Link, NavLink, Outlet} from "react-router-dom"
import {Box, Container} from "@chakra-ui/react"
import {Disclosure, Menu, Transition} from "@headlessui/react"
import {Bars3Icon, BellIcon, XMarkIcon} from "@heroicons/react/24/outline"

import {appInfo} from "@backend/types/base"
import {useAuth} from "@auth/context"
import {WorkModal} from "@modules/user/components/WorkModal"

import {defaultProfileImg} from "../constants"
import {navRoutes, userRoutes} from "../routes"


export default function AppPanel() {
    let {user, isAdmin, logout} = useAuth()

    return (
        <Box className="min-h-full" bg="gray.100">
            <Disclosure as="nav" className="bg-white shadow-sm">
                {({open}) => (
                    <>
                        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                            <div className="flex h-16 justify-between">
                                <Link to="/" className="flex flex-shrink-0 items-center">
                                    <img
                                        className="block h-8 w-auto lg:hidden"
                                        src={appInfo.logo}
                                        alt={appInfo.title}
                                    />
                                    <img
                                        className="hidden h-8 w-auto lg:block"
                                        src={appInfo.logo}
                                        alt={appInfo.title}
                                    />
                                </Link>
                                <div className="hidden sm:-my-px sm:ml-6 sm:flex sm:space-x-8">
                                    {navRoutes(isAdmin).map((item, i) => (
                                        <NavLink
                                            key={item.id}
                                            to={item.path}
                                            className={({isActive}) => clsx(
                                                isActive
                                                    ? "border-indigo-500 text-gray-900"
                                                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700",
                                                "inline-flex items-center border-b-2 px-1 pt-1 text-sm font-medium"
                                            )}
                                        >
                                            {item.handle.title}
                                        </NavLink>
                                    ))}
                                </div>

                                <div className="hidden sm:ml-6 sm:flex sm:items-center">
                                    <button
                                        type="button"
                                        className="relative rounded-full bg-white p-1 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                    >
                                        <span className="absolute -inset-1.5"/>
                                        <span className="sr-only">View notifications</span>
                                        <BellIcon className="h-6 w-6" aria-hidden="true"/>
                                    </button>

                                    {/* Profile dropdown */}
                                    <Menu as="div" className="relative ml-3">
                                        <div>
                                            <Menu.Button
                                                className="relative flex rounded-full bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                                                <span className="absolute -inset-1.5"/>
                                                <span className="sr-only">Open user menu</span>
                                                <img className="h-8 w-8 rounded-full"
                                                     src={user.publicInfo.profilePhoto || defaultProfileImg}
                                                     alt="Profile Photo"/>
                                            </Menu.Button>
                                        </div>
                                        <Transition
                                            as={Fragment}
                                            enter="transition ease-out duration-200"
                                            enterFrom="transform opacity-0 scale-95"
                                            enterTo="transform opacity-100 scale-100"
                                            leave="transition ease-in duration-75"
                                            leaveFrom="transform opacity-100 scale-100"
                                            leaveTo="transform opacity-0 scale-95"
                                        >
                                            <Menu.Items
                                                className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                                {userRoutes.map((item) => (
                                                    <Menu.Item key={item.id}>
                                                        {({active}) => (
                                                            <Link
                                                                to={item.path}
                                                                className={clsx(
                                                                    active ? "bg-gray-100" : "",
                                                                    "block px-4 py-2 text-sm text-gray-700"
                                                                )}
                                                            >
                                                                {item.handle.title}
                                                            </Link>
                                                        )}
                                                    </Menu.Item>
                                                ))}
                                                <Menu.Item>
                                                    {({active}) => (
                                                        <span
                                                            onClick={logout}
                                                            className={clsx(
                                                                active ? "bg-gray-100" : "",
                                                                "cursor-pointer block px-4 py-2 text-sm text-gray-700"
                                                            )}
                                                        >
                                                            Logout
                                                        </span>
                                                    )}
                                                </Menu.Item>
                                            </Menu.Items>
                                        </Transition>
                                    </Menu>
                                </div>
                                <div className="-mr-2 flex items-center sm:hidden">
                                    {/* Mobile menu button */}
                                    <Disclosure.Button
                                        className="relative inline-flex items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                                        <span className="absolute -inset-0.5"/>
                                        <span className="sr-only">Open main menu</span>
                                        {open ? (
                                            <XMarkIcon className="block h-6 w-6" aria-hidden="true"/>
                                        ) : (
                                            <Bars3Icon className="block h-6 w-6" aria-hidden="true"/>
                                        )}
                                    </Disclosure.Button>
                                </div>
                            </div>
                        </div>

                        <Disclosure.Panel className="sm:hidden">
                            <div className="space-y-1 pb-3 pt-2">
                                {navRoutes(isAdmin).map((item, i) => (
                                    <Disclosure.Button
                                        key={item.id}
                                        as={NavLink}
                                        to={item.path}
                                        className={({isActive}) => (clsx(
                                            isActive
                                                ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                                                : "border-transparent text-gray-600 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800",
                                            "block border-l-4 py-2 pl-3 pr-4 text-base font-medium"
                                        ))}
                                        aria-current={i === 0 ? "page" : undefined}
                                    >
                                        {item.handle.title}
                                    </Disclosure.Button>
                                ))}
                            </div>
                            <div className="border-t border-gray-200 pb-3 pt-4">
                                <div className="flex items-center px-4">
                                    <div className="flex-shrink-0">
                                        <img className="h-10 w-10 rounded-full"
                                             src={user.publicInfo.profilePhoto || defaultProfileImg} alt=""/>
                                    </div>
                                    <div className="ml-3">
                                        <div
                                            className="text-base font-medium text-gray-800">{user.publicInfo.displayName}</div>
                                        {/*<div className="text-sm font-medium text-gray-500">{user.email}</div>*/}
                                    </div>
                                    <button
                                        type="button"
                                        className="relative ml-auto flex-shrink-0 rounded-full bg-white p-1 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                    >
                                        <span className="absolute -inset-1.5"/>
                                        <span className="sr-only">View notifications</span>
                                        <BellIcon className="h-6 w-6" aria-hidden="true"/>
                                    </button>
                                </div>
                                <div className="mt-3 space-y-1">
                                    {userRoutes.map((item) => (
                                        <Disclosure.Button
                                            key={item.id}
                                            as={Link}
                                            to={item.path}
                                            className="block px-4 py-2 text-base font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                                        >
                                            {item.handle.title}
                                        </Disclosure.Button>
                                    ))}
                                    <Disclosure.Button
                                        onClick={logout}
                                        className="w-full text-left block px-4 py-2 text-base font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                                    >
                                        Logout
                                    </Disclosure.Button>
                                </div>
                            </div>
                        </Disclosure.Panel>
                    </>
                )}
            </Disclosure>

            <Container className="py-10" maxW="container.xl" style={{minHeight: "calc(100vh - 8rem)"}}>
                <Outlet/>
            </Container>

            {!user.hasWorkData && <WorkModal user={user}/>}

            <footer className="bg-white h-16 flex flex-col justify-center items-center">
                <Container maxW="container.xl">
                    <p className="text-center text-xs leading-5 text-gray-500 prose max-w-full">
                        A project of <a target="_blank"
                                        href="http://www.legaltechdesign.com">Stanford
                        Legal Design Lab</a>
                        &nbsp;All Rights Reserved {new Date().getFullYear()}
                    </p>
                </Container>
            </footer>
        </Box>
    )
}
